import { useMemo, useState } from 'react';
import {
  Check,
  Download,
  RotateCcw,
  Clock,
  Calendar,
  Wrench,
  ShieldCheck,
  Sparkles,
  ChevronRight,
} from 'lucide-react';
import { useMaintenance } from '../hooks/useMaintenance';
import { maintenanceData } from '../content';

type ChecklistType = 'daily' | 'weekly' | 'monthly';

type MaintenanceItem = {
  id: string;
  task: string;
  estimatedTime: string;
  description?: string;
};

type MaintenanceChecklist = {
  title: string;
  description?: string;
  items: MaintenanceItem[];
};

type MaintenanceDataShape = {
  checklists: Record<ChecklistType, MaintenanceChecklist>;
};

function BluePanel({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        'relative overflow-hidden rounded-[28px] border border-dtf-accent/20',
        'bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]',
        'shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]',
        className,
      ].join(' ')}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function StatPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-dtf-text-muted">
        <span className="text-dtf-accent">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-base font-semibold text-white">{value}</div>
    </div>
  );
}

function InfoCard({
  title,
  description,
  iconClassName,
}: {
  title: string;
  description: string;
  iconClassName: string;
}) {
  return (
    <BluePanel>
      <div className="p-5">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-black/20">
            <Calendar className={`h-5 w-5 ${iconClassName}`} />
          </div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        <p className="text-sm leading-7 text-dtf-text-muted">{description}</p>
      </div>
    </BluePanel>
  );
}

export function Maintenance() {
  const [activeTab, setActiveTab] = useState<ChecklistType>('daily');
  const { isTaskCompleted, toggleTask, getCompletionRate, resetToday, exportProgress } =
    useMaintenance();

  const typedMaintenanceData = maintenanceData as unknown as MaintenanceDataShape;
  const checklist = typedMaintenanceData.checklists[activeTab];

  const completedCount = useMemo(() => {
    return checklist.items.filter((item) => isTaskCompleted(item.id)).length;
  }, [checklist.items, isTaskCompleted]);

  const completionRate = getCompletionRate(checklist.items.length);

  const totalEstimatedMinutes = useMemo(() => {
    return checklist.items.reduce((total: number, item: MaintenanceItem) => {
      const match = item.estimatedTime?.match(/\d+/);
      const minutes = match ? parseInt(match[0], 10) : NaN;
      return total + (isNaN(minutes) ? 0 : minutes);
    }, 0);
  }, [checklist.items]);

  const handleExport = () => {
    const data = exportProgress();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dtf-maintenance-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tabConfig: Record<
    ChecklistType,
    {
      label: string;
      description: string;
    }
  > = {
    daily: {
      label: 'Daily',
      description: 'Quick routine checks before or during production.',
    },
    weekly: {
      label: 'Weekly',
      description: 'Deeper cleaning and inspection to catch issues early.',
    },
    monthly: {
      label: 'Monthly',
      description: 'Longer maintenance to preserve accuracy and reliability.',
    },
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <BluePanel className="mb-6">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
                <Wrench className="h-3.5 w-3.5" />
                Maintenance
              </div>

              <h1 className="mb-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                Maintenance Tracker
              </h1>

              <p className="text-lg leading-8 text-dtf-text">
                Keep your DTF equipment running smoothly with repeatable daily, weekly, and
                monthly maintenance checklists.
              </p>
            </div>

            <div className="grid gap-3 sm:min-w-[280px] sm:grid-cols-2">
              <StatPill
                icon={<Clock className="h-4 w-4" />}
                label="Estimated Time"
                value={`~${totalEstimatedMinutes} min`}
              />
              <StatPill
                icon={<ShieldCheck className="h-4 w-4" />}
                label="Completion"
                value={`${completionRate}%`}
              />
            </div>
          </div>
        </div>
      </BluePanel>

      <div className="mb-6 grid gap-3 md:grid-cols-3">
        {(['daily', 'weekly', 'monthly'] as ChecklistType[]).map((tab) => {
          const active = activeTab === tab;

          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`group rounded-[24px] border px-5 py-5 text-left transition ${
                active
                  ? 'border-dtf-blue/35 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.16),transparent_55%),linear-gradient(180deg,rgba(10,22,46,0.98)_0%,rgba(7,15,31,0.98)_100%)] shadow-[0_0_24px_rgba(37,99,235,0.18)]'
                  : 'border-white/10 bg-black/20 hover:border-dtf-blue/25 hover:bg-black/30'
              }`}
            >
              <div className="mb-2 flex items-center justify-between gap-3">
                <span
                  className={`text-lg font-semibold ${
                    active ? 'text-white' : 'text-dtf-text'
                  }`}
                >
                  {tabConfig[tab].label}
                </span>

                <ChevronRight
                  className={`h-4 w-4 transition ${
                    active
                      ? 'translate-x-0 text-dtf-blue'
                      : 'text-dtf-text-muted group-hover:translate-x-0.5 group-hover:text-white'
                  }`}
                />
              </div>

              <p className={`text-sm leading-6 ${active ? 'text-dtf-text' : 'text-dtf-text-muted'}`}>
                {tabConfig[tab].description}
              </p>
            </button>
          );
        })}
      </div>

      <BluePanel className="mb-6">
        <div className="border-b border-white/8 px-6 py-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">{checklist.title}</h2>
              {checklist.description && (
                <p className="mt-1 text-sm text-dtf-text-muted">{checklist.description}</p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-2 text-sm text-dtf-text">
                {completedCount} of {checklist.items.length} tasks completed
              </div>

              {activeTab === 'daily' && (
                <button
                  onClick={resetToday}
                  className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-4 py-2 text-sm font-medium text-dtf-text-muted transition hover:border-dtf-accent/30 hover:text-white"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-dtf-text-muted">Checklist progress</span>
            <span className="font-semibold text-white">{completionRate}%</span>
          </div>

          <div className="h-3 overflow-hidden rounded-full bg-black/30">
            <div
              className="h-full rounded-full bg-[linear-gradient(90deg,rgba(59,130,246,0.95)_0%,rgba(245,158,11,0.95)_100%)] transition-all duration-500"
              style={{ width: `${completionRate}%` }}
            />
          </div>
        </div>
      </BluePanel>

      <BluePanel className="mb-6">
        <div className="border-b border-white/8 px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-xl font-bold text-white">Checklist Tasks</h3>
            <span className="text-sm text-dtf-text-muted">
              Tap a task to mark it complete
            </span>
          </div>
        </div>

        <div className="divide-y divide-white/6">
          {checklist.items.map((item: MaintenanceItem) => {
            const completed = isTaskCompleted(item.id);

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => toggleTask(item.id)}
                className={`flex w-full items-start gap-4 px-6 py-5 text-left transition hover:bg-white/[0.025] ${
                  completed ? 'opacity-70' : ''
                }`}
              >
                <div
                  className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border-2 transition ${
                    completed
                      ? 'border-dtf-accent bg-dtf-accent text-dtf-dark'
                      : 'border-dtf-blue/40 bg-dtf-blue/10 text-transparent'
                  }`}
                >
                  <Check className="h-4 w-4" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <p
                      className={`text-base font-medium ${
                        completed ? 'text-dtf-text line-through' : 'text-white'
                      }`}
                    >
                      {item.task}
                    </p>

                    <div className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-3 py-1.5 text-sm text-dtf-text-muted">
                      <Clock className="h-4 w-4 text-dtf-accent" />
                      {item.estimatedTime}
                    </div>
                  </div>

                  {item.description && (
                    <p className="pr-4 text-sm leading-7 text-dtf-text-muted">
                      {item.description}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </BluePanel>

      <div className="mb-8 flex justify-end">
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-5 py-3 font-medium text-dtf-text-muted transition hover:border-dtf-blue/30 hover:bg-black/30 hover:text-white"
        >
          <Download className="h-4 w-4" />
          Export Progress
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <InfoCard
          title="Daily"
          description="Start-of-day checks and quick upkeep. Helps prevent the most common production problems before they begin."
          iconClassName="text-dtf-accent"
        />
        <InfoCard
          title="Weekly"
          description="Deeper cleaning and inspection tasks. Good for catching wear, buildup, and small issues before they grow."
          iconClassName="text-dtf-blue"
        />
        <InfoCard
          title="Monthly"
          description="Longer maintenance items like calibration, inspection, and planned replacement tasks that protect print consistency."
          iconClassName="text-dtf-green"
        />
      </div>

      <div className="mt-8 rounded-3xl border border-white/10 bg-black/20 px-5 py-4">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0 text-dtf-blue" />
          <p className="text-sm leading-7 text-dtf-text-muted">
            Stay consistent with the same checklist rhythm every week. Good maintenance habits
            usually prevent far more downtime than emergency troubleshooting ever can.
          </p>
        </div>
      </div>
    </div>
  );
}